/**
 * KbdHint — rendert eine Sequenz von Tasten als kbd-Pills.
 * Spec: docs/design-spec.md §9 (Shortcuts).
 *
 * Beispiele:
 *   <KbdHint keys="g d" />          ->  [G] [D]
 *   <KbdHint keys="cmd k" />        ->  [⌘] [K]
 *   <KbdHint keys="cmd + shift + a" /> -> [⌘] [⇧] [A]
 *
 * Splitte an `+` und Whitespace, mappe Symbole, capitalize einzelne Letter.
 */
import { useMemo } from "react";
import { cn } from "@/lib/utils";

export interface KbdHintProps {
  /** Tasten als Space-/Plus-getrennter String. */
  keys: string;
  /** Pill-Groesse (default `'sm'`). */
  size?: "sm" | "base";
  className?: string;
}

const SYMBOL_MAP: Record<string, string> = {
  cmd: "⌘",
  meta: "⌘",
  command: "⌘",
  shift: "⇧",
  alt: "⌥",
  opt: "⌥",
  option: "⌥",
  ctrl: "⌃",
  control: "⌃",
  enter: "↵",
  return: "↵",
  esc: "Esc",
  escape: "Esc",
  tab: "Tab",
  space: "Space",
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  backspace: "⌫",
  delete: "⌦",
};

function normalizeKey(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  if (SYMBOL_MAP[lower]) return SYMBOL_MAP[lower];
  // Single letter -> uppercase
  if (trimmed.length === 1) return trimmed.toUpperCase();
  // Sonst Original (z.B. "F1", "PgUp")
  return trimmed;
}

function splitKeys(input: string): string[] {
  // Splitte an `+` ODER Whitespace; ignoriere leere Tokens
  return input
    .split(/[\s+]+/)
    .map((k) => k.trim())
    .filter(Boolean)
    .map(normalizeKey)
    .filter(Boolean);
}

const SIZE_CLASS = {
  sm: "h-5 px-1.5 text-[11px]",
  base: "h-6 px-2 text-xs",
};

export function KbdHint({ keys, size = "sm", className }: KbdHintProps) {
  const parts = useMemo(() => splitKeys(keys), [keys]);

  if (parts.length === 0) return null;

  return (
    <span
      className={cn("inline-flex items-center gap-1 align-middle", className)}
      aria-label={`Tastenkombination: ${parts.join(" plus ")}`}
    >
      {parts.map((part, i) => (
        <kbd
          key={`${part}-${i}`}
          className={cn(
            "inline-flex items-center justify-center rounded-xs border border-border-default bg-bg-subtle font-mono font-medium leading-none text-text-secondary",
            SIZE_CLASS[size],
          )}
        >
          {part}
        </kbd>
      ))}
    </span>
  );
}
